import React, { useMemo, useRef, useState } from 'react';
import { Download, Filter, Upload } from 'lucide-react';
import { Heading } from '../../layout';
import { TabNavigation } from '../../reusable';
import { assignCashFlowToEntries, conciliateEntry } from '../../../services/treasuryAPI';

const currency = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const SECTION_CONFIG = {
  overview: { title: 'Finanzas - Overview', subtitle: 'Consolidado ejecutivo con detalle transaccional y herramientas de gestión.' },
  treasury: { title: 'Finanzas - Tesorería', subtitle: 'Movimientos de caja y bancos, conciliación y clasificación contable.' },
  accounting: { title: 'Finanzas - Contabilidad', subtitle: 'Asientos, control de registros y clasificación por cuenta contable.' },
  taxes: { title: 'Finanzas - Impuestos', subtitle: 'Detalle tributario, obligaciones y conciliación tributaria.' },
  purchases: { title: 'Finanzas - Compras', subtitle: 'Órdenes, compromisos y ejecución presupuestaria por proveedor.' },
  budget: { title: 'Finanzas - Presupuesto', subtitle: 'Seguimiento presupuesto vs real, variaciones y reclasificaciones.' },
  payroll: { title: 'Finanzas - Nómina', subtitle: 'Control de costos laborales y seguimiento detallado por concepto.' },
  statements: { title: 'Finanzas - Estados Financieros', subtitle: 'Indicadores de estados financieros con desglose consolidado y analítico.' },
  financing: { title: 'Finanzas - Financiamiento', subtitle: 'Control de deuda, cuotas, costo financiero y acciones de gestión.' },
};

const BASE_ROWS = [
  { id: 1, date: '2026-03-01', document: 'TRX-1001', description: 'Cobro cliente minería', account: '1.1.01 Banco', category: 'Operación', amount: 3200000, status: 'pendiente', month: '2026-03', source: 'ERP' },
  { id: 2, date: '2026-03-02', document: 'TRX-1002', description: 'Pago proveedor resinas', account: '2.1.01 CxP', category: 'Compras', amount: -1800000, status: 'pendiente', month: '2026-03', source: 'ERP' },
  { id: 3, date: '2026-03-03', document: 'TRX-1003', description: 'Nómina quincena', account: '5.1.03 Nómina', category: 'Nómina', amount: -2200000, status: 'conciliado', month: '2026-03', source: 'Banco' },
  { id: 4, date: '2026-03-05', document: 'TRX-1004', description: 'Venta contado retail', account: '4.1.01 Ventas', category: 'Ventas', amount: 1450000, status: 'pendiente', month: '2026-03', source: 'POS' },
  { id: 5, date: '2026-03-06', document: 'TRX-1005', description: 'Pago impuesto mensual', account: '2.1.05 Impuestos', category: 'Impuestos', amount: -770000, status: 'pendiente', month: '2026-03', source: 'SII' },
  { id: 6, date: '2026-03-08', document: 'TRX-1006', description: 'Interés crédito banco', account: '5.3.01 Gasto financiero', category: 'Financiamiento', amount: -240000, status: 'conciliado', month: '2026-03', source: 'Banco' },
];

const buildRowsBySection = (sectionKey) =>
  BASE_ROWS.map((row) => ({
    ...row,
    section: sectionKey,
    tag:
      sectionKey === 'taxes'
        ? 'Fiscal'
        : sectionKey === 'payroll'
        ? 'RRHH'
        : sectionKey === 'financing'
        ? 'Deuda'
        : 'General',
  }));

const cardStyles = (palette) => ({
  border: `1px solid ${palette.border}`,
  borderRadius: '12px',
  padding: '12px',
  background: palette.panel,
});

const FinanceSectionInteractiveWorkspace = ({ isDarkMode = false, sectionKey }) => {
  const config = SECTION_CONFIG[sectionKey] || SECTION_CONFIG.overview;
  const [rows, setRows] = useState(() => buildRowsBySection(sectionKey));
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [classificationAccount, setClassificationAccount] = useState('1.1.01 Banco');
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const palette = isDarkMode
    ? { panel: '#18181b', border: '#2f2f35', text: '#f4f4f5', muted: '#a1a1aa', soft: '#111113' }
    : { panel: '#ffffff', border: '#e5e7eb', text: '#111827', muted: '#6b7280', soft: '#f8fafc' };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const byStatus = statusFilter === 'all' ? true : row.status === statusFilter;
      const q = search.trim().toLowerCase();
      const bySearch = !q
        ? true
        : [row.document, row.description, row.account, row.category, row.tag]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(q));
      return byStatus && bySearch;
    });
  }, [rows, search, statusFilter]);

  const consolidated = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const current = map.get(row.month) || { month: row.month, inflows: 0, outflows: 0, records: 0 };
      if (Number(row.amount) >= 0) current.inflows += Number(row.amount);
      else current.outflows += Math.abs(Number(row.amount));
      current.records += 1;
      map.set(row.month, current);
    });
    return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
  }, [rows]);

  const totals = useMemo(() => {
    const inflows = rows.filter((row) => row.amount >= 0).reduce((acc, row) => acc + Number(row.amount), 0);
    const outflows = rows.filter((row) => row.amount < 0).reduce((acc, row) => acc + Math.abs(Number(row.amount)), 0);
    const reconciled = rows.filter((row) => row.status === 'conciliado').length;
    return {
      inflows,
      outflows,
      net: inflows - outflows,
      reconciliationRate: rows.length ? (reconciled / rows.length) * 100 : 0,
    };
  }, [rows]);

  const toggleSelection = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const selectAllFiltered = () => {
    setSelectedIds(filteredRows.map((row) => row.id));
  };

  const clearSelection = () => setSelectedIds([]);

  const handleConciliate = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      await Promise.all(selectedIds.map((id) => conciliateEntry(id).catch(() => null)));
      setRows((prev) => prev.map((row) => (selectedIds.includes(row.id) ? { ...row, status: 'conciliado' } : row)));
    } finally {
      setBusy(false);
    }
  };

  const handleClassify = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      await assignCashFlowToEntries(selectedIds, 1).catch(() => null);
      setRows((prev) =>
        prev.map((row) =>
          selectedIds.includes(row.id) ? { ...row, account: classificationAccount, category: 'Clasificado' } : row
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const handleExportCsv = () => {
    const header = ['id', 'date', 'document', 'description', 'account', 'category', 'amount', 'status', 'month', 'source', 'tag'];
    const lines = [header.join(',')];
    filteredRows.forEach((row) => {
      lines.push(header.map((key) => `"${String(row[key] ?? '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance_${sectionKey}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const payload = JSON.parse(String(reader.result || '[]'));
        if (!Array.isArray(payload)) return;
        const normalized = payload
          .filter((row) => row && row.id && row.date && row.description)
          .map((row) => ({
            id: Number(row.id),
            date: String(row.date),
            document: String(row.document || `IMP-${row.id}`),
            description: String(row.description),
            account: String(row.account || 'Sin clasificar'),
            category: String(row.category || 'Importado'),
            amount: Number(row.amount || 0),
            status: String(row.status || 'pendiente'),
            month: String(row.month || String(row.date).slice(0, 7)),
            source: String(row.source || 'Import'),
            tag: String(row.tag || 'Importado'),
            section: sectionKey,
          }));
        setRows((prev) => [...normalized, ...prev]);
      } catch (error) {
        // Ignore malformed file without blocking UI.
      } finally {
        event.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const indicatorTab = (
    <div style={{ display: 'grid', gap: '12px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: '10px' }}>
        <div style={cardStyles(palette)}><p style={{ margin: 0, fontSize: '11px', color: palette.muted }}>Ingresos</p><p style={{ margin: '8px 0 0', fontSize: '24px', color: '#10b981', fontWeight: 800 }}>{currency(totals.inflows)}</p></div>
        <div style={cardStyles(palette)}><p style={{ margin: 0, fontSize: '11px', color: palette.muted }}>Egresos</p><p style={{ margin: '8px 0 0', fontSize: '24px', color: '#ef4444', fontWeight: 800 }}>{currency(totals.outflows)}</p></div>
        <div style={cardStyles(palette)}><p style={{ margin: 0, fontSize: '11px', color: palette.muted }}>Resultado</p><p style={{ margin: '8px 0 0', fontSize: '24px', color: totals.net >= 0 ? '#10b981' : '#ef4444', fontWeight: 800 }}>{currency(totals.net)}</p></div>
        <div style={cardStyles(palette)}><p style={{ margin: 0, fontSize: '11px', color: palette.muted }}>Conciliación</p><p style={{ margin: '8px 0 0', fontSize: '24px', color: '#3b82f6', fontWeight: 800 }}>{totals.reconciliationRate.toFixed(1)}%</p></div>
      </div>

      <div style={{ ...cardStyles(palette), background: palette.soft }}>
        <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: palette.text }}>Indicadores consolidados</p>
        <div style={{ marginTop: '10px', display: 'grid', gap: '8px' }}>
          {consolidated.map((row) => (
            <div key={row.month} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 110px', gap: '8px', alignItems: 'center' }}>
              <span style={{ color: palette.muted, fontSize: '12px' }}>{row.month}</span>
              <div style={{ height: '10px', borderRadius: '999px', background: '#e5e7eb' }}><div style={{ width: `${Math.min(100, (row.inflows / Math.max(1, totals.inflows)) * 100)}%`, height: '10px', borderRadius: '999px', background: '#10b981' }} /></div>
              <div style={{ height: '10px', borderRadius: '999px', background: '#e5e7eb' }}><div style={{ width: `${Math.min(100, (row.outflows / Math.max(1, totals.outflows)) * 100)}%`, height: '10px', borderRadius: '999px', background: '#ef4444' }} /></div>
              <span style={{ color: palette.muted, fontSize: '12px', textAlign: 'right' }}>{row.records} reg.</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const consolidatedTab = (
    <div style={{ ...cardStyles(palette) }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
        <Filter size={15} />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar documento, cuenta o categoría..." style={{ height: '32px', borderRadius: '8px', border: `1px solid ${palette.border}`, padding: '0 10px', minWidth: '280px' }} />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ height: '32px', borderRadius: '8px', border: `1px solid ${palette.border}`, padding: '0 8px' }}>
          <option value="all">Todos</option>
          <option value="pendiente">Pendiente</option>
          <option value="conciliado">Conciliado</option>
        </select>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Mes', 'Registros', 'Ingresos', 'Egresos', 'Resultado'].map((h) => (
              <th key={h} style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${palette.border}`, fontSize: '11px', color: palette.muted }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {consolidated.map((row) => (
            <tr key={row.month}>
              <td style={{ padding: '8px' }}>{row.month}</td>
              <td style={{ padding: '8px' }}>{row.records}</td>
              <td style={{ padding: '8px', color: '#10b981' }}>{currency(row.inflows)}</td>
              <td style={{ padding: '8px', color: '#ef4444' }}>{currency(row.outflows)}</td>
              <td style={{ padding: '8px', fontWeight: 700, color: row.inflows - row.outflows >= 0 ? '#10b981' : '#ef4444' }}>{currency(row.inflows - row.outflows)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const detailTab = (
    <div style={{ ...cardStyles(palette), display: 'grid', gap: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={selectAllFiltered} style={{ height: '32px', borderRadius: '8px', border: `1px solid ${palette.border}`, background: 'transparent', cursor: 'pointer' }}>Seleccionar filtrados</button>
          <button type="button" onClick={clearSelection} style={{ height: '32px', borderRadius: '8px', border: `1px solid ${palette.border}`, background: 'transparent', cursor: 'pointer' }}>Limpiar selección</button>
          <span style={{ fontSize: '12px', color: palette.muted }}>{selectedIds.length} seleccionados</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" onClick={handleConciliate} disabled={busy || !selectedIds.length} style={{ height: '32px', borderRadius: '8px', border: 'none', background: '#10b981', color: '#fff', padding: '0 10px', cursor: 'pointer' }}>Conciliar</button>
          <select value={classificationAccount} onChange={(e) => setClassificationAccount(e.target.value)} style={{ height: '32px', borderRadius: '8px', border: `1px solid ${palette.border}` }}>
            <option>1.1.01 Banco</option>
            <option>2.1.01 Cuentas por Pagar</option>
            <option>4.1.01 Ventas</option>
            <option>5.1.03 Nómina</option>
          </select>
          <button type="button" onClick={handleClassify} disabled={busy || !selectedIds.length} style={{ height: '32px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', padding: '0 10px', cursor: 'pointer' }}>Clasificar cuenta</button>
          <button type="button" onClick={handleExportCsv} style={{ height: '32px', borderRadius: '8px', border: `1px solid ${palette.border}`, background: 'transparent', padding: '0 10px', cursor: 'pointer' }}><Download size={14} style={{ verticalAlign: 'middle' }} /> Exportar</button>
          <button type="button" onClick={() => fileInputRef.current?.click()} style={{ height: '32px', borderRadius: '8px', border: `1px solid ${palette.border}`, background: 'transparent', padding: '0 10px', cursor: 'pointer' }}><Upload size={14} style={{ verticalAlign: 'middle' }} /> Importar</button>
          <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['', 'Fecha', 'Documento', 'Descripción', 'Cuenta', 'Categoría', 'Monto', 'Estado', 'Origen'].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${palette.border}`, fontSize: '11px', color: palette.muted }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr key={row.id}>
                <td style={{ padding: '8px' }}><input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleSelection(row.id)} /></td>
                <td style={{ padding: '8px' }}>{row.date}</td>
                <td style={{ padding: '8px' }}>{row.document}</td>
                <td style={{ padding: '8px' }}>{row.description}</td>
                <td style={{ padding: '8px' }}>{row.account}</td>
                <td style={{ padding: '8px' }}>{row.category}</td>
                <td style={{ padding: '8px', color: row.amount >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>{currency(row.amount)}</td>
                <td style={{ padding: '8px' }}>
                  <span style={{ borderRadius: '999px', padding: '2px 8px', fontSize: '11px', color: row.status === 'conciliado' ? '#065f46' : '#92400e', background: row.status === 'conciliado' ? '#d1fae5' : '#fef3c7' }}>
                    {row.status}
                  </span>
                </td>
                <td style={{ padding: '8px' }}>{row.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const tabs = [
    { key: 'indicators', label: 'Indicadores', content: indicatorTab },
    { key: 'consolidated', label: 'Consolidado', content: consolidatedTab },
    { key: 'detail', label: 'Detalle y Operación', content: detailTab },
  ];

  return (
    <div>
      <Heading level={2} isDarkMode={isDarkMode}>{config.title}</Heading>
      <TabNavigation tabs={tabs} defaultTab="indicators" isDarkMode={isDarkMode} />
    </div>
  );
};

export default FinanceSectionInteractiveWorkspace;
